import type { TaskFormInterface } from '@components/RequisitionCreate/JSONSchemaForm/JSONSchemaForm';

import { Clinician, IClinic } from './clinician';
import { PatientRequisitionDetails } from './patient';
import { FormattedProduct } from './product';
import { Requisition } from './requisition';
import type { ProviderUserProfile } from './user';

export type KeycloakRealm = 'dev' | 'natera-prd' | 'natera-sbx' | 'natera-stg' | 'prd' | 'sbx' | 'stg' | 'tst';

export enum AppTenant {
  MYOME = 'myome',
  NATERA = 'natera',
}

export enum AppEnvironment {
  LOCAL = 'local',
  DEV = 'dev',
  TEST = 'tst',
  STAGING = 'stg',
  SANDBOX = 'sbx',
  PROD = 'prd',
}

export interface AppSettings {
  apiurl: string;
  baseUrlRegex: RegExp;
  environment: AppEnvironment;
  googleMapsApiKey: string;
  turnstileSiteKey: string;
  hubspotAccountId: string | null;
  keycloakRealm: KeycloakRealm;
  sentryDSN: string | null;
  tenant: AppTenant;
  openreplayProjectKey: string;
  openreplayIngestPoint: string;
}

export interface AppStateUserProfile extends ProviderUserProfile {
  fullName: string;
}

export interface AppState {
  // User Profile
  userProfile: AppStateUserProfile | null;
  hasPartnershipOrdering: boolean;
  adminClinics: IClinic[]; // OrganizationRole.ADMIN
  orderingProviderClinics: IClinic[]; // OrganizationRole.CLINICIAN
  // Patient Management
  formattedProductsList: FormattedProduct[];
  newRequisition?: Requisition | null;
  requisitionTaskForm?: TaskFormInterface;
  patientsMap: Record<string, PatientRequisitionDetails | null>;
  // For Ordering Provider
  clinicalTeam: Clinician[];
  // For Admin
  users: Clinician[];
  // Derived States
  orgDefaultDelegates: Clinician[]; // From users state
}

export const Actions = {
  USER_PROFILE_UPDATED: 'userProfile/UPDATED',
} as const;

type UserProfileUpdated = { type: typeof Actions.USER_PROFILE_UPDATED; payload: ProviderUserProfile };

type SetNewRequisition = {
  type: 'SET_NEW_REQUISITION';
  payload: Requisition | null;
};
type SetUsers = { type: 'SET_USERS'; payload: Clinician[] };
type SetFormattedProducts = { type: 'SET_FORMATTED_PRODUCTS_LIST'; payload: FormattedProduct[] };
type SetRequisitionTaskForm = { type: 'SET_REQUISITION_TASK_FORM'; payload: TaskFormInterface | undefined };
type PatchRequisitionTaskForm = {
  type: 'PATCH_REQUISITION_TASK_FORM';
  payload: Pick<TaskFormInterface, 'formdata' | 'fileRegistry'>;
};
type SetPatientDetail = {
  type: 'SET_PATIENT_DETAIL';
  payload: {
    patientId: string;
    result: PatientRequisitionDetails | null;
  };
};
type SetClinicalTeam = {
  type: 'SET_CLINICAL_TEAM';
  payload: Clinician[];
};
type AddClinicalTeamMember = {
  type: 'ADD_CLINICAL_TEAM_MEMBER';
  payload: Clinician;
};
type RemoveClinicalTeamMember = {
  type: 'REMOVE_CLINICAL_TEAM_MEMBER';
  payload: string; // clinician_id
};
type UpdateUser = {
  type: 'UPDATE_USER';
  payload: Clinician;
};

export type AppActions =
  | UserProfileUpdated
  | SetFormattedProducts
  | SetNewRequisition
  | SetRequisitionTaskForm
  | PatchRequisitionTaskForm
  | SetPatientDetail
  | SetClinicalTeam
  | AddClinicalTeamMember
  | RemoveClinicalTeamMember
  | SetUsers
  | UpdateUser;
